import React, { useRef, useState } from 'react';
import { X, UploadCloud, Loader2, Layers } from 'lucide-react';
import { splitDoc, assembleSplit, portalErrorMessage } from '../api/portal';

/**
 * "Everything in one file?" — upload one combined scan, tag each page to a document type, and the
 * server assembles one PDF per type (passport forced to a single page). Assembled docs are handed
 * back to ApplyPage via onAssembled to populate the matching slots.
 *
 * Props: slots (the SLOTS config), onAssembled(docs), onClose.
 */
export default function SplitModal({ slots, onAssembled, onClose }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [splitId, setSplitId] = useState(null);
  const [pages, setPages] = useState([]); // [{ index, thumbnail }]
  const [assign, setAssign] = useState({}); // index -> type | 'skip'

  async function onFile(file) {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { setError('That file is larger than 15 MB.'); return; }
    setBusy(true); setError('');
    try {
      const res = await splitDoc(file);
      setSplitId(res.splitId);
      setPages(res.pages || []);
      const init = {};
      (res.pages || []).forEach((p) => { init[p.index] = 'skip'; });
      setAssign(init);
    } catch (err) { setError(portalErrorMessage(err, 'Could not read that file.')); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  }

  async function done() {
    const assignments = Object.entries(assign)
      .filter(([, type]) => type && type !== 'skip')
      .map(([index, type]) => ({ pageIndex: Number(index), type }));
    if (assignments.length === 0) { setError('Tag at least one page to a document type.'); return; }
    setBusy(true); setError('');
    try {
      const res = await assembleSplit(splitId, assignments);
      onAssembled(res.docs || []);
    } catch (err) { setError(portalErrorMessage(err, 'Could not split that file.')); }
    finally { setBusy(false); }
  }

  return (
    <div className="split-overlay" role="dialog" aria-modal="true" aria-label="Split a combined document" onClick={onClose}>
      <div className="split-modal" onClick={(e) => e.stopPropagation()}>
        <div className="split-head">
          <span className="split-title"><Layers size={18} /> Split one file into documents</span>
          <button type="button" className="split-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        </div>

        {error && <div className="apply-error" role="alert">{error}</div>}

        {!splitId ? (
          <div className="split-body">
            <p className="apply-hint">Upload the combined scan (all your documents in one file). We’ll show every page so you can label each one.</p>
            <input ref={inputRef} type="file" hidden accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              onChange={(e) => onFile(e.target.files?.[0] || null)} aria-label="Upload combined file" />
            <button type="button" className="apply-drop" onClick={() => inputRef.current?.click()} disabled={busy}>
              {busy ? <Loader2 size={26} className="spin" /> : <UploadCloud size={26} />}
              <span>{busy ? 'Reading pages…' : 'Tap to upload the combined file'}</span>
              <small>PDF, Word or a photo — up to 15 MB</small>
            </button>
          </div>
        ) : (
          <div className="split-body">
            <p className="apply-hint">Choose what each page is. Skip anything you don’t need.</p>
            <div className="split-grid">
              {pages.map((p) => (
                <div className="split-page" key={p.index}>
                  <img src={p.thumbnail} alt={`Page ${p.index + 1}`} />
                  <span className="split-page-num">Page {p.index + 1}</span>
                  <select value={assign[p.index] || 'skip'}
                    onChange={(e) => setAssign((a) => ({ ...a, [p.index]: e.target.value }))}>
                    <option value="skip">— Skip —</option>
                    {slots.map((s) => <option key={s.type} value={s.type}>{s.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <button type="button" className="apply-btn" onClick={done} disabled={busy}>
              {busy ? <Loader2 size={18} className="spin" /> : null} Use these documents
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
